import { get, reduce } from 'lodash';
import React from 'react';
import ReactDOM from 'react-dom';
import { Provider } from 'react-redux';

import { BREADCRUMB_NAVIGATION_LINK_TYPES } from 'constants/breadcrumbNavigation';
import { COLLECTION_TYPES } from 'constants/collectionPage';
import { CAMPAIGN_NAMES } from 'constants/personalizations';
import CollectionParentContainer from 'containers/CollectionParentContainer';
import * as actions from 'state/actions';
import BreadcrumbsUtil from 'utils/Breadcrumbs';
import DynamicYield from 'utils/DynamicYield';
import Log from 'utils/Log';
import Settings from 'utils/Settings';
import { parseQueryString } from 'utils/Url';

const ParentCollectionController = class {
  static get parentCollectionData() {
    const data = get(window, ['ALLBIRDS', 'parentCollection']);

    if (!data) {
      Log.error(
        new Error(
          'Controllers:ParentCollectionController.parentCollectionData -- missing parent collection data!'
        )
      );
    }

    return data;
  }

  static get collectionTitle() {
    return get(ParentCollectionController, ['parentCollectionData', 'title']);
  }

  static get collectionHandle() {
    return get(ParentCollectionController, ['parentCollectionData', 'handle']);
  }

  static get faqItems() {
    return get(ParentCollectionController, ['parentCollectionData', 'faqItems']);
  }

  static get descriptionModule() {
    return get(ParentCollectionController, [
      'parentCollectionData',
      'localizedDescriptionModule',
      Settings.currentLocale,
    ]);
  }

  static get urlParams() {
    return parseQueryString();
  }

  static initFiltersFromUrlParams(store) {
    store.dispatch(actions.initCollectionPageFilters({ urlParams: this.urlParams }));
  }

  static setCollectionPageData(store) {
    store.dispatch(
      actions.setCollectionPageData({
        data: {
          handle: ParentCollectionController.collectionHandle,
          type: COLLECTION_TYPES.PARENT_COLLECTION,
        },
      })
    );
  }

  static updateDynamicYieldContext = () => {
    const { breadcrumbTrail } = BreadcrumbsUtil.getBreadcrumbTrail({
      type: BREADCRUMB_NAVIGATION_LINK_TYPES.COLLECTION,
      handle: ParentCollectionController.collectionHandle,
    });

    const breakcrumbMap = reduce(
      breadcrumbTrail,
      (arr, breadcrumb) => {
        if (get(breadcrumb, 'type') !== BREADCRUMB_NAVIGATION_LINK_TYPES.HOME) {
          arr.push(get(breadcrumb, 'label'));
        }
        return arr;
      },
      []
    );

    DynamicYield.updateContext('CATEGORY', breakcrumbMap);
  };

  static initCollectionPage = store => {
    ParentCollectionController.initFiltersFromUrlParams(store);
    ParentCollectionController.setCollectionPageData(store);
  };

  static initCatalog = store => {
    store.dispatch(
      actions.initCatalogParentCollection({
        parentCollectionData: ParentCollectionController.parentCollectionData,
        parentCollectionSortOrder: Settings.parentCollectionSortOrder,
      })
    );
  };

  static mountCollectionParentContainer = store => {
    ReactDOM.render(
      <Provider store={store}>
        <CollectionParentContainer handle={ParentCollectionController.collectionHandle} />
      </Provider>,
      document.getElementById('parent-collection-container')
    );
  };

  static fetchDYRecs = store => {
    store.dispatch(
      actions.fetchPersonalizations({
        names: [
          CAMPAIGN_NAMES.COLLECTION_RECS,
          CAMPAIGN_NAMES.COLLECTION_STORY_CARDS,
          CAMPAIGN_NAMES.COLLECTION_PERSONALIZED_MERCHANDISING,
          CAMPAIGN_NAMES.COLLECTION_STICKY_SUBNAV,
          CAMPAIGN_NAMES.COLLECTION_COLORWAY_CARDS,
        ],
      })
    );
  };

  static addStructuredData = store => {
    store.dispatch(
      actions.setCollectionPageStructuredData({
        faqItems: ParentCollectionController.faqItems,
        description: ParentCollectionController.descriptionModule,
        name: ParentCollectionController.collectionTitle,
      })
    );
  };

  static init = store => {
    ParentCollectionController.initCatalog(store);
    ParentCollectionController.initCollectionPage(store);
    ParentCollectionController.mountCollectionParentContainer(store);
    ParentCollectionController.updateDynamicYieldContext();
    ParentCollectionController.fetchDYRecs(store);
    ParentCollectionController.addStructuredData(store);
  };
};

export default ParentCollectionController;
